<template lang="pug">
	.audioTracker--plugins--equalizer-chart
		svg(ref="chart" :width="width" :height="height" xmlns="http://www.w3.org/2000/svg")
			// Axes and Grid Lines
			g.axes
				// Vertical Grid Lines (Frequency Ticks)
				line(
					v-for="(label, index) in xAxis.labels"
					:x1="getX(label.value)"
					:y1="padding"
					:x2="getX(label.value)"
					:y2="height - padding"
					:stroke="gridColor"
					stroke-dasharray="2,2"
				)
				// X-Axis Line
				line(
					:x1="padding"
					:y1="height - padding"
					:x2="width"
					:y2="height - padding"
					:stroke="axisColor"
				)
				// Frequency Labels (X-Axis)
				text(
					v-for="(label, index) in xAxis.labels"
					:key="`x-axis-${index}`"
					:x="getX(label.value)"
					:y="height - padding / 2"
					:fill="labelColor"
					text-anchor="middle"
				) {{ formatFrequency(label.value) }}
				// Y-Axis Line
				line(
					:x1="padding"
					:y1="padding"
					:x2="padding"
					:y2="height - padding"
					:stroke="axisColor"
				)
				// dB Labels (Y-Axis)
				text(
					v-for="(label, index) in yAxis.labels"
					:key="`y-axis-${index}`"
					:x="padding / 2"
					:y="getY(label.value)"
					:fill="labelColor"
					text-anchor="end"
				) {{ label.value }}
				// Axis Labels
				text(
					v-if="yAxis.label"
					:x="10"
					:y="padding / 2"
					:fill="labelColor"
					text-anchor="start"
					transform=`rotate(-90, ${padding / 2}, ${height / 2})`
				) {{ yAxis.label || 'dB' }}
				text(
					v-if="xAxis.label"
					:x="width / 2"
					:y="height - padding / 8"
					:fill="labelColor"
					text-anchor="middle"
				) {{ xAxis.label || 'hZ' }}

			// Data Series
			g.series
				g.serie(
					v-for="(serie, serieIndex) in innerSeries"
					:key="serieIndex"
				)
					// Area Fill (for area and spline-area types)
					path(
						v-if="['area', 'spline-area'].includes(serie.type)"
						:d="serie.type === 'area' ? getAreaPath(serieIndex) : getSplineAreaPath(serieIndex)"
						:fill="serie.fillColor"
						:opacity="serie.opacity || 0.3"
						pointer-events="none"
					)

					// Lines (spline or straight)
					path(
						v-if="serie.type === 'spline' || !serie.hidePoints",
						:d="['spline', 'spline-area'].includes(serie.type) ? getSplinePath(serieIndex) : getLinePath(serieIndex)"
						:stroke="serie.lineColor"
						:stroke-width="serie.lineWidth"
						fill="none"
						pointer-events="none"
					)

					// Points
					circle(
						v-for="(point, pointIndex) in getSeriePoints(serieIndex)"
						:key="`${serieIndex}-${pointIndex}`"
						:cx="getX(point.frequency)"
						:cy="getY(point.dB)"
						:r="pointRadius"
						:fill="point.color || serie.pointColor"
						v-if="!point.hidden && !serie.hidePoints && serie.editable && !point.preventEdit"
						pointer-events="all"
						@mousedown="startDragging(point, serieIndex)"
					)

					// Point Labels
					text(
						v-if="!serie.hidePoints && serie.showPointLabels"
						v-for="(point, pointIndex) in getSeriePoints(serieIndex)"
						:key="`label-${serieIndex}-${pointIndex}`"
						:x="getX(point.frequency)"
						:y="getLabelY(point)"
						:fill="serie.pointLabelColor || '#000'"
						text-anchor="middle"
						pointer-events="none"
					) {{ point.label || point.dB }}
</template>

<script>
import _ from 'lodash';

export default {
	translationCategory: 'audioTracker.plugins.DynamicEqChart',
	name: "audioTracker.plugins.EqualizerChart",
	props: {
		width: { type: Number, default: 900 },
		height: { type: Number, default: 400 },
		padding: { type: Number, default: 50 },
		xAxis: {
			type: Object,
			default: () => ({
				labels: [
					{ value: 80 },
					{ value: 250 },
					{ value: 800 },
					{ value: 2000 },
					{ value: 6000 },
					{ value: 16000 }
				],
				min: 20,
				max: 20000,
				label: 'Frequency (Hz)'
			}),
		},
		yAxis: {
			type: Object,
			default: () => ({
				labels: Array.from({ length: 13 }, (_, i) => ({
					value: -30 + i * 5
				})),
				min: -30,
				max: 30,
				label: 'Amplitude (dB)'
			}),
		},
		series: {
			type: Array,
			default: () => []
		},
		axisColor: { type: String, default: "#777" },
		gridColor: { type: String, default: "#999" },
		labelColor: { type: String, default: "#666" },
		pointRadius: { type: Number, default: 8 },
		roundTo: { type: Number, default: 0.1 }
	},
	data() {
		return {
			dragging: {
				isDragging: false,
				point: null,
				serieIndex: null,
				pointIndex: null,
				dB: null,
				frequency: null
			},
			innerSeries: _.cloneDeep(this.series)
		};
	},
	watch: {
		'series': {
			handler(v) {
				this.innerSeries = _.cloneDeep(v);
			},
			deep: true
		}
	},
	
	mounted() {
		if (__DEBUG__)
			window.dynamicEqChart = this;

		this.$emitChanging = _.debounce(this.emitChanging, 50, {
			maxWait: 500,
			leading: false,
			trailing: true
		});

		document.addEventListener("mousemove", this.onMouseMove);
		document.addEventListener("mouseup", this.onMouseUp);
	},
	beforeDestroy() {
		document.removeEventListener("mousemove", this.onMouseMove);
		document.removeEventListener("mouseup", this.onMouseUp);
		delete this.$emitChanging;
	},
	methods: {
		getSeriePoints(serieIndex) {
			let points = _.orderBy(this.series[serieIndex]?.points || [], f => f.frequency);

			if (!this.dragging.isDragging || this.dragging.serieIndex !== serieIndex) return points;

			let draggingRealPoint = (this.series[serieIndex]?.points || [])[this.dragging.pointIndex];

			return points?.map((f) => {
				if (f === draggingRealPoint) {
					return {
						...f,
						dB: this.dragging.dB,
						frequency: this.dragging.frequency
					}
				}
				return f;
			}) || [];
		},
		// Round a value to the nearest multiple of the roundTo prop
		roundValue(value) {
			const factor = 1 / this.roundTo;
			return Math.round(value * factor) / factor;
		},
		// Get the vertical position of the label, adjusting to prevent overlap with the chart boundary
		getLabelY(point) {
			const y = this.getY(point.dB); // Y coordinate of the point
			const offset = 10; // Offset for the label
			if (y - offset < this.padding) {
				// If the label is too close to the top, display it below the point
				return y + offset;
			}
			// Default: display the label above the point
			return y - offset;
		},
		// Convert frequency to X coordinate (logarithmic scale)
		getX(frequency) {
			const scale =
				(Math.log10(frequency) - Math.log10(this.xAxis.min)) /
				(Math.log10(this.xAxis.max) - Math.log10(this.xAxis.min));
			return this.padding + scale * (this.width - this.padding);
		},
		// Convert dB value to Y coordinate (linear scale)
		getY(dB) {
			const scale =
				(dB - this.yAxis.min) / (this.yAxis.max - this.yAxis.min);
			return (
				this.height - this.padding - scale * (this.height - 2 * this.padding)
			);
		},
		// Format frequency for display (e.g., 1000 -> 1k)
		formatFrequency(freq) {
			if (freq >= 1000) return `${freq / 1000}k`;
			return freq;
		},
		// Generate path for straight lines
		getLinePath(serieIndex) {
			let points = this.getSeriePoints(serieIndex);
			if (!points.length) return '';
			const zeroY = this.getY(0); // Y coordinate for 0 dB

			// Add virtual start and end points
			const virtualPoints = [
				{ frequency: this.xAxis.min, dB: 0 }, // Start point (0 dB)
				...points,
				{ frequency: this.xAxis.max, dB: 0 } // End point (0 dB)
			];

			const path = virtualPoints
				.map((point, index) => {
					const x = this.getX(point.frequency);
					const y = point.dB === 0 ? zeroY : this.getY(point.dB); // Use zeroY for 0 dB
					return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
				})
				.join(' ');
			return path;
		},
		// Generate path for area (with filled background)
		getAreaPath(serieIndex) {
			let points = this.getSeriePoints(serieIndex);

			if (!points.length) return '';
			const zeroY = this.getY(0); // Y coordinate for 0 dB

			// Add virtual start and end points
			const virtualPoints = [
				{ frequency: this.xAxis.min, dB: 0 }, // Start point (0 dB)
				...points,
				{ frequency: this.xAxis.max, dB: 0 } // End point (0 dB)
			];

			const path = virtualPoints
				.map((point, index) => {
					const x = this.getX(point.frequency);
					const y = point.dB === 0 ? zeroY : this.getY(point.dB); // Use zeroY for 0 dB
					return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
				})
				.join(' ');

			// Close the path to 0 dB line
			return `${path} L ${this.getX(this.xAxis.max)} ${zeroY} L ${this.getX(this.xAxis.min)} ${zeroY} Z`;
		},
		// Generate path for smooth lines (Bezier curves)
		getSplinePath(serieIndex) {
			let points = this.getSeriePoints(serieIndex);
			let serie = this.series[serieIndex];

			if (!points.length) return '';
			let zeroDb = serie.zeroDb || 0;

			const zeroY = this.getY(zeroDb); // Y coordinate for 0 dB

			// Add virtual start and end points
			const extendedPoints = [
				{ frequency: this.xAxis.min, dB: zeroDb }, // Start point
				...points,
				{ frequency: this.xAxis.max, dB: zeroDb } // End point
			];

			// Start path with the first point
			let path = `M ${this.getX(extendedPoints[0].frequency)} ${zeroY}`;

			// Calculate Bezier curves for each pair of points
			for (let i = 0; i < extendedPoints.length - 1; i++) {
				const p0 = extendedPoints[i];
				const p1 = extendedPoints[i + 1];

				// Calculate control points for the curve
				const cp1X = this.getX(p0.frequency) + (this.getX(p1.frequency) - this.getX(p0.frequency)) / 3;
				const cp1Y = this.getY(p0.dB);
				const cp2X = this.getX(p1.frequency) - (this.getX(p1.frequency) - this.getX(p0.frequency)) / 3;
				const cp2Y = this.getY(p1.dB);

				// Add a Bezier curve segment
				path += ` C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${this.getX(p1.frequency)} ${this.getY(p1.dB)}`;
			}

			return path;
		},
		// Generate path for smooth filled areas (Bezier curves)
		getSplineAreaPath(serieIndex) {
			let points = this.getSeriePoints(serieIndex);
			let serie = this.series[serieIndex];

			if (!points.length) return '';
			let zeroDb = serie.zeroDb || 0;

			const zeroY = this.getY(zeroDb); // Y coordinate for 0 dB

			// Add virtual start and end points
			const extendedPoints = [
				{ frequency: this.xAxis.min, dB: zeroDb }, // Start point
				...points,
				{ frequency: this.xAxis.max, dB: zeroDb } // End point
			];

			// Start path with the first point
			let path = `M ${this.getX(extendedPoints[0].frequency)} ${zeroY}`;

			// Add smooth lines using Bezier curves
			for (let i = 0; i < extendedPoints.length - 1; i++) {
				const p0 = extendedPoints[i];
				const p1 = extendedPoints[i + 1];

				// Calculate control points for the curve
				const cp1X = this.getX(p0.frequency) + (this.getX(p1.frequency) - this.getX(p0.frequency)) / 3;
				const cp1Y = this.getY(p0.dB);
				const cp2X = this.getX(p1.frequency) - (this.getX(p1.frequency) - this.getX(p0.frequency)) / 3;
				const cp2Y = this.getY(p1.dB);

				// Add a Bezier curve segment
				path += ` C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${this.getX(p1.frequency)} ${this.getY(p1.dB)}`;
			}

			// Close the path to the 0 dB line
			path += ` L ${this.getX(extendedPoints[extendedPoints.length - 1].frequency)} ${zeroY} Z`;

			let baseLineSerieIndex = serie.baseLineSerie ? this.series.findIndex(f => f?.name === serie?.baseLineSerie) : -1;
			if (baseLineSerieIndex !== -1) {
				let basePoints = [
					{ frequency: this.xAxis.min, dB: 0 },
					...this.getSeriePoints(baseLineSerieIndex),
					{ frequency: this.xAxis.max, dB: 0 }
				].reverse();

				path += ` M ${this.getX(basePoints[0].frequency)} ${zeroY} Z`;

				for (let i = 0; i < basePoints.length - 1; i++) {
					const p0 = basePoints[i];
					const p1 = basePoints[i + 1];

					// Calculate control points for the curve
					const cp1X = this.getX(p0.frequency) + (this.getX(p1.frequency) - this.getX(p0.frequency)) / 3;
					const cp1Y = this.getY(p0.dB);
					const cp2X = this.getX(p1.frequency) - (this.getX(p1.frequency) - this.getX(p0.frequency)) / 3;
					const cp2Y = this.getY(p1.dB);

					// Add a Bezier curve segment
					path += ` C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${this.getX(p1.frequency)} ${this.getY(p1.dB)}`;
				}

				path += `L ${this.getX(basePoints[basePoints.length - 1].frequency)} ${zeroY}`;
			}

			return path;
		},
		// Start dragging a point
		startDragging(point, serieIndex) {
			let points = this.series[serieIndex]?.points || [];
			let pointIndex = points.findIndex(f => f === point);
			if (point.preventEdit) return; // Do nothing if editing is disabled
			this.dragging.isDragging = true;
			this.dragging.point = point;
			this.dragging.dB = this.dragging.point.dB;
			this.dragging.frequency = this.dragging.point.frequency;
			this.dragging.serieIndex = serieIndex;
			this.dragging.pointIndex = pointIndex;
		},
		// Handle mouse movement to drag points
		onMouseMove(e) {
			if (!this.dragging.isDragging) return;
			const rect = this.$refs.chart.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;

			// Calculate frequency and dB values
			let frequency = Math.min(
				Math.max(
					this.xAxis.min,
					Math.pow(
						10,
						Math.log10(this.xAxis.min) +
							((x - this.padding) / (this.width - this.padding)) *
								(Math.log10(this.xAxis.max) - Math.log10(this.xAxis.min))
					)
				),
				this.xAxis.max
			);
			let dB = Math.min(
				Math.max(
					this.yAxis.min,
					this.yAxis.min +
						((this.height - this.padding - y) / (this.height - 2 * this.padding)) *
							(this.yAxis.max - this.yAxis.min)
				),
				this.yAxis.max
			);

			// Round the values
			frequency = this.roundValue(frequency);
			dB = this.roundValue(dB);

			// Update the point
			this.dragging.frequency = frequency;
			this.dragging.dB = dB;
			
			this.$emitChanging();
		},
		// Handle mouse release
		onMouseUp() {
			if (!this.dragging.isDragging) return;
			this.$emit('change', { point: this.dragging.point, frequency: this.dragging.frequency, dB: this.dragging.dB, serie: this.innerSeries[this.dragging.serieIndex], pointIndex: this.dragging.pointIndex });
			this.dragging.isDragging = false;
			this.dragging.point = null;
			this.dragging.serieIndex = null;
		},
		emitChanging() {
			if (!this.dragging.isDragging) return;
			
			this.$emit('changing', { point: this.dragging.point, frequency: this.dragging.frequency, dB: this.dragging.dB, serie: this.innerSeries[this.dragging.serieIndex], pointIndex: this.dragging.pointIndex });
		}
	}
};
</script>

<style style="scss">
.audioTracker--plugins--equalizer-chart {
	user-select: none;
}
</style>
